import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  IconButton,
  Image,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { IoClose } from "react-icons/io5";

const TEXT_GAP = "5px";
const AUTO_DISMISS_MS = 6000;

export default function PushNotificationToast() {
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);

  const shellBg = useColorModeValue("rgba(255,255,255,0.98)", "rgba(28,28,30,0.98)");
  const shellBorder = useColorModeValue("rgba(15,23,42,0.08)", "rgba(255,255,255,0.12)");
  const appLabel = useColorModeValue("gray.500", "whiteAlpha.600");
  const titleColor = useColorModeValue("gray.900", "white");
  const bodyColor = useColorModeValue("gray.700", "whiteAlpha.800");
  const shadow = useColorModeValue(
    "0 8px 32px rgba(15,23,42,0.14), 0 2px 8px rgba(15,23,42,0.06)",
    "0 12px 40px rgba(0,0,0,0.45)"
  );

  const dismiss = useCallback(() => setNotification(null), []);

  const openNotification = useCallback(() => {
    if (!notification?.url) return;
    navigate(notification.url);
    dismiss();
  }, [dismiss, navigate, notification]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;

    const onMessage = (event) => {
      if (event.data?.type !== "PUSH_SHOW") return;
      setNotification({
        title: event.data.title || "Payinapo",
        body: event.data.body || "",
        url: event.data.url || "/Messages",
      });
    };

    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    if (!notification) return undefined;
    const timer = window.setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [dismiss, notification]);

  if (!notification) return null;

  return (
    <Box
      position="fixed"
      top="calc(8px + env(safe-area-inset-top, 0px))"
      left={3}
      right={3}
      zIndex={2000}
      maxW="420px"
      mx="auto"
      bg={shellBg}
      border="1px solid"
      borderColor={shellBorder}
      borderRadius="2xl"
      boxShadow={shadow}
      backdropFilter="blur(20px) saturate(180%)"
      px={3}
      py={2.5}
      role="status"
      aria-live="polite"
      cursor="pointer"
      onClick={openNotification}
    >
      <Flex align="flex-start" gap={2.5}>
        <Image
          src="/logo192.png"
          alt=""
          boxSize="40px"
          borderRadius="lg"
          flexShrink={0}
          objectFit="cover"
        />
        <Flex direction="column" flex={1} minW={0} gap={TEXT_GAP}>
          <Flex align="center" justify="space-between" gap={2} m={0} p={0} minH={0} lineHeight={1}>
            <Text
              as="span"
              fontSize="10px"
              fontWeight="bold"
              letterSpacing="0.08em"
              color={appLabel}
              lineHeight={1}
              m={0}
              p={0}
            >
              PAYINAPO
            </Text>
            <Text as="span" fontSize="10px" color={appLabel} lineHeight={1} m={0} p={0}>
              now
            </Text>
          </Flex>
          <Text
            fontSize="13px"
            fontWeight="semibold"
            color={titleColor}
            noOfLines={1}
            lineHeight={1}
            m={0}
            p={0}
          >
            {notification.title}
          </Text>
          <Text fontSize="13px" color={bodyColor} noOfLines={2} lineHeight={1} m={0} p={0}>
            {notification.body}
          </Text>
          <Text fontSize="10px" color={appLabel} noOfLines={1} lineHeight={1} m={0} p={0}>
            → {notification.url}
          </Text>
        </Flex>
        <IconButton
          aria-label="Dismiss notification"
          icon={<IoClose size={16} />}
          size="xs"
          variant="ghost"
          mt={-1}
          mr={-1}
          onClick={(event) => {
            event.stopPropagation();
            dismiss();
          }}
        />
      </Flex>
    </Box>
  );
}
